import React, { useEffect, useMemo, useState } from 'react';
import { getPortfolioEquity } from '../api/portfolio';

const PERIODS = [
  { value: 'current_month', label: 'Mês atual' },
  { value: 'previous_month', label: 'Mês anterior' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'all', label: 'Todo o histórico' },
];

const money = (value, signed = false) => {
  if (!Number.isFinite(Number(value))) return '—';
  const amount = Number(value);
  const sign = signed && amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${sign}R$ ${Math.abs(amount).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const shortDate = value => {
  const [year, month, day] = String(value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}` : '—';
};

function EquitySvg({ points }) {
  const W = 760;
  const H = 238;
  const PL = 58;
  const PR = 16;
  const PT = 16;
  const PB = 36;
  const innerW = W - PL - PR;
  const innerH = H - PT - PB;
  const values = points.map(point => Number(point.value)).filter(Number.isFinite);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min;
  const padding = spread ? spread * 0.16 : Math.max(Math.abs(max) * 0.04, 1);
  const floor = Math.max(0, min - padding);
  const ceiling = max + padding;
  const range = ceiling - floor || 1;
  const xAt = index => PL + (index / Math.max(1, points.length - 1)) * innerW;
  const yAt = value => PT + ((ceiling - value) / range) * innerH;
  const line = points.map((point, index) => (
    `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(1)},${yAt(Number(point.value)).toFixed(1)}`
  )).join(' ');
  const area = `${line} L${xAt(points.length - 1).toFixed(1)},${(H - PB).toFixed(1)} L${PL},${(H - PB).toFixed(1)} Z`;
  const labels = points.map((point, index) => {
    const show = index === 0 || index === points.length - 1 || index % Math.max(1, Math.ceil(points.length / 5)) === 0;
    if (!show) return null;
    const last = index === points.length - 1;
    return (
      <text
        key={`label-${point.date}`}
        x={xAt(index)}
        y={H - 14}
        textAnchor={index === 0 ? 'start' : last ? 'end' : 'middle'}
        fill={last ? '#d7b768' : '#526684'}
        fontSize="10"
        fontFamily="'JetBrains Mono', monospace"
      >
        {shortDate(point.date)}
      </text>
    );
  });

  return (
    <svg className="portfolio-equity-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Evolução do patrimônio teórico">
      <defs>
        <linearGradient id="portfolio-equity-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5b9dff" stopOpacity=".24" />
          <stop offset="100%" stopColor="#5b9dff" stopOpacity=".01" />
        </linearGradient>
      </defs>
      {[0, .33, .66, 1].map((step, index) => {
        const value = ceiling - step * range;
        const y = PT + step * innerH;
        return (
          <g key={`grid-${index}`}>
            <line x1={PL} x2={W - PR} y1={y} y2={y} stroke="#17263d" strokeWidth="1" />
            <text x={PL - 8} y={y + 3} textAnchor="end" fill="#526684" fontSize="9" fontFamily="'JetBrains Mono', monospace">
              {money(value)}
            </text>
          </g>
        );
      })}
      <path d={area} fill="url(#portfolio-equity-fill)" />
      <path d={line} fill="none" stroke="#6eabff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((point, index) => {
        if (index !== 0 && index !== points.length - 1) return null;
        return (
          <circle key={`point-${point.date}`} cx={xAt(index)} cy={yAt(Number(point.value))} r="4" fill="#0d1422" stroke="#8fc1ff" strokeWidth="2">
            <title>{`${shortDate(point.date)} · ${money(Number(point.value))}`}</title>
          </circle>
        );
      })}
      {labels}
    </svg>
  );
}

export default function PortfolioEquityChart({ strategy = 'all' }) {
  const [period, setPeriod] = useState('current_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getPortfolioEquity(period, strategy)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError('Não foi possível carregar a evolução do patrimônio.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period, strategy]);

  const points = useMemo(
    () => (data?.points || []).filter(point => Number.isFinite(Number(point.value))),
    [data],
  );
  const stats = useMemo(() => {
    if (!points.length) return { current: null, delta: null, pct: null };
    const first = Number(points[0].value);
    const current = Number(points[points.length - 1].value);
    const delta = current - first;
    return { current, delta, pct: first ? (delta / first) * 100 : null };
  }, [points]);
  const positive = (stats.delta || 0) >= 0;
  const selectedLabel = PERIODS.find(item => item.value === period)?.label;

  return (
    <section className="portfolio-equity-card" aria-label="Evolução do patrimônio">
      <div className="portfolio-equity-head">
        <div>
          <div className="portfolio-equity-title">Evolução do patrimônio</div>
          <div className="portfolio-equity-subtitle">
            Posições teóricas marcadas a mercado · {selectedLabel}
          </div>
        </div>
        <div className="portfolio-equity-stats">
          <div>
            <span>Último valor</span>
            <strong>{money(stats.current)}</strong>
          </div>
          <div>
            <span>Variação</span>
            <strong className={positive ? 'positive' : 'negative'}>
              {money(stats.delta, true)}
              {stats.pct != null && <small>{`${stats.pct >= 0 ? '+' : ''}${stats.pct.toFixed(2)}%`}</small>}
            </strong>
          </div>
        </div>
      </div>

      <div className="portfolio-equity-periods" role="group" aria-label="Período do gráfico">
        {PERIODS.map(item => (
          <button
            key={item.value}
            type="button"
            className={period === item.value ? 'active' : ''}
            onClick={() => setPeriod(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="portfolio-equity-state"><div className="portfolio-equity-spinner" />Calculando evolução…</div>
      ) : error ? (
        <div className="portfolio-equity-state portfolio-equity-state--error">{error}</div>
      ) : points.length < 1 ? (
        <div className="portfolio-equity-state">
          <strong>Sem dados neste período</strong>
          <span>Quando houver posições com histórico de preços, a curva aparecerá aqui.</span>
        </div>
      ) : (
        <div className="portfolio-equity-scroll"><EquitySvg points={points} /></div>
      )}
      <div className="portfolio-equity-note">Sem aportes ou caixa: o gráfico mostra apenas o valor teórico das posições.</div>
    </section>
  );
}